import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToken,
} from '@chakra-ui/react';

import Loading from '@/client/components/media/Loading';
import { useLazyLoadStylesheet } from '@/client/services/hooks/useLazyLoad';
import { useUploadImage } from '@/client/services/hooks/shared/images/useUploadImage';

// TODO: Combine this and AuthoringImageEditor.tsx into a single component

interface ImageEditorProps {
  modalTitle?: string;
  image: string;
  setImage: (image: string) => void;
  onSuccessfulUpload: (cloudfrontLink: string) => void;
  defaultAspectRatio?: number;
}

export default function ImageEditor({
  modalTitle,
  image,
  setImage,
  onSuccessfulUpload,
  defaultAspectRatio = 73 / 20,
}: ImageEditorProps) {
  useLazyLoadStylesheet('/public/styles/image-styles.css');
  const [warmNeutal100] = useToken('colors', ['warmNeutal.100']);
  const { uploadImageMutation } = useUploadImage();
  const { status } = uploadImageMutation;

  return (
    <Modal size="full" isOpen={image !== ''} onClose={() => setImage('')}>
      <ModalOverlay />
      <ModalContent height="full">
        <ModalCloseButton />

        <ModalHeader>
          <Text>{modalTitle}</Text>
        </ModalHeader>

        <ModalBody padding={12}>
          {status === 'loading' && <Loading />}

          {status !== 'loading' && (
            <FilerobotImageEditor
              source={image}
              onBeforeSave={() => false}
              theme={{
                palette: {
                  primary: warmNeutal100,
                  'icons-primary': warmNeutal100,
                  'icons-secondary': warmNeutal100,
                  'borders-primary': warmNeutal100,
                  'borders-secondary': warmNeutal100,
                  'borders-strong': warmNeutal100,
                  'accent-primary': warmNeutal100,
                  'accent-secondary': warmNeutal100,
                  'button-primary': warmNeutal100,
                  success: warmNeutal100,
                },
              }}
              onSave={(editedImageObject) => {
                const dataURItoBlob = (dataURI: string) => {
                  const binary = window.atob(dataURI.split(',')[1]);

                  const array = [];

                  for (let i = 0; i < binary.length; i += 1) {
                    array.push(binary.charCodeAt(i));
                  }

                  return new Blob([new Uint8Array(array)], { type: 'image/jpeg' });
                };

                if (editedImageObject && editedImageObject.imageBase64) {
                  const file = new File(
                    [dataURItoBlob(editedImageObject.imageBase64)],
                    'uploaded-img.jpeg',
                  );

                  uploadImageMutation.mutateAsync({
                    file,
                    errorMessage: 'Error uploading image',
                    successActions: onSuccessfulUpload,
                  });
                }
              }}
              onClose={() => setImage('')}
              annotationsCommon={{
                fill: '#ff0000',
              }}
              Text={{ text: 'Filerobot...' }}
              Rotate={{ angle: 90, componentType: 'slider' }}
              Crop={{
                noPresets: true,
                ratio: defaultAspectRatio,
              }}
              tabsIds={[TABS.ADJUST /* TABS.FILTERS, TABS.RESIZE */]}
              defaultTabId={TABS.ADJUST}
              defaultToolId={TOOLS.CROP}
              savingPixelRatio={4}
              previewPixelRatio={window.devicePixelRatio}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
